import { z } from "zod";

/**
 * Seat maths shared by the email/LinkedIn seat modals (PREC-3714).
 *
 * Three different seat numbers exist and had been mixed up by the callers:
 * - `base`  — included in the plan, never billed.
 * - `paid`  — billed add-on seats; this is the Stripe subscription item quantity.
 * - `bonus` — granted by an admin, never billed.
 *
 * The modal input is a **seat total** (`base + paid`) and the API takes a **paid amount**, so every
 * conversion goes through here rather than being re-derived per caller.
 */

/** Seats shown in the modal input. Bonus seats are free, so they are never part of it. */
export const toSeatTotal = (paidAmount: number, baseAmount: number): number =>
	paidAmount + baseAmount;

/** The Stripe quantity to bill: everything the user asked for above the plan-included seats. */
export const toPaidAmount = (seatTotal: number, baseAmount: number): number =>
	seatTotal - baseAmount;

/**
 * Seats the modal opens on. A pending decrease is a *paid* amount, so it needs the base added back
 * before it can be shown in a total-valued input.
 */
export const toInitialSeatTotal = (
	paidAmount: number,
	baseAmount: number,
	pendingAmount?: number | null
): number => toSeatTotal(pendingAmount ?? paidAmount, baseAmount);

/**
 * `Number("")` is `0` and `Number("abc")` is `NaN`, either of which submits a negative paid amount
 * once the base is subtracted. Collapse both to the plan minimum instead.
 */
export const parseSeatInput = (raw: string, baseAmount: number): number => {
	const parsed = Number.parseInt(raw, 10);

	return Number.isNaN(parsed) ? baseAmount : parsed;
};

/** The seat total can never drop below the seats the plan already includes. */
export const createSeatTotalSchema = (baseAmount: number) =>
	z.object({
		amount: z
			.number()
			.int({ message: "Enter a whole number of seats" })
			.min(baseAmount, { message: `You cannot go below the ${baseAmount} seats in your plan` }),
	});

export type SeatTotalForm = z.infer<ReturnType<typeof createSeatTotalSchema>>;

/** Prop shape shared by both seat modals — no ambiguous "amount", so callers cannot mis-seed it. */
export interface SenderSeatModalProps {
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
	/** Billed add-on seats today (the Stripe item quantity). */
	paidAmount: number;
	/** Seats included in the plan. */
	baseAmount: number;
	/** Admin-granted free seats, shown but never billed. */
	bonusAmount: number;
	/** A pending decrease, expressed as a paid amount. */
	pendingAmount?: number | null;
	pendingEffectiveAt?: string | null;
	/** Target company for admin edits; omitted means "the signed-in user's company". */
	companyId?: number;
}
